import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsContext = createContext();

// Phase 133.79 — Branding (Farben, Fonts, Preloads) sofort auf <html> anwenden.
// Wird sowohl beim ersten Settings-Fetch als auch beim localStorage-Hydrate
// aufgerufen, damit die gecachte Branding-Anwendung ohne "Flash" passiert.
const _applyBrandToRoot = (data) => {
  if (!data || !data.brand) return;
  try {
    const candidates = [data.brand.logo_url];
    const home = data.home || {};
    const about = home.about || {};
    if (about.image_1) candidates.push(about.image_1);
    if (about.image_2) candidates.push(about.image_2);
    if (home.slider_slides && home.slider_slides[0]?.image_url) {
      candidates.push(home.slider_slides[0].image_url);
    }
    candidates.filter(Boolean).forEach((u) => {
      const href = resolveImageUrl(u);
      if (!href) return;
      if (document.querySelector(`link[rel="preload"][as="image"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      link.fetchpriority = 'high';
      document.head.appendChild(link);
    });
  } catch { /* never block */ }

  const root = document.documentElement;
  const brand = data.brand;
  if (brand.primary_color) root.style.setProperty('--brand-primary', brand.primary_color);
  if (brand.secondary_color) root.style.setProperty('--brand-secondary', brand.secondary_color);
  if (brand.accent_color) root.style.setProperty('--brand-accent', brand.accent_color);
  if (brand.font_headline) {
    const hf = brand.font_headline;
    const stack = /helvetica/i.test(hf) ? `'${hf}', Helvetica, Arial, sans-serif` : `'${hf}', sans-serif`;
    root.style.setProperty('--font-headline', stack);
  }
  if (brand.font_body) {
    const bf = brand.font_body;
    const stack = /helvetica/i.test(bf) ? `'${bf}', Helvetica, Arial, sans-serif` : `'${bf}', sans-serif`;
    root.style.setProperty('--font-body', stack);
  }
  if (brand.font_scale) root.style.setProperty('--font-scale', String(brand.font_scale));

  const typo = brand.typography || {};
  const setFont = (name, val) => {
    if (!val) return;
    const stack = /helvetica/i.test(val) ? `'${val}', Helvetica, Arial, sans-serif` : `'${val}', sans-serif`;
    root.style.setProperty(name, stack);
  };
  const toggle = (cls, on) => on ? root.classList.add(cls) : root.classList.remove(cls);

  setFont('--font-h1', typo.h1_font);
  setFont('--font-h2', typo.h2_font);
  setFont('--font-h3', typo.h3_font);
  setFont('--font-body-el', typo.body_font);
  toggle('typo-h1-font-custom', !!typo.h1_font);
  toggle('typo-h2-font-custom', !!typo.h2_font);
  toggle('typo-h3-font-custom', !!typo.h3_font);

  if (typo.h1_weight) root.style.setProperty('--weight-h1', String(typo.h1_weight));
  if (typo.h2_weight) root.style.setProperty('--weight-h2', String(typo.h2_weight));
  if (typo.h3_weight) root.style.setProperty('--weight-h3', String(typo.h3_weight));
  if (typo.body_weight) root.style.setProperty('--weight-body', String(typo.body_weight));
  toggle('typo-h1-weight-custom', !!typo.h1_weight);
  toggle('typo-h2-weight-custom', !!typo.h2_weight);
  toggle('typo-h3-weight-custom', !!typo.h3_weight);

  if (typo.h1_size) { root.style.setProperty('--size-h1', typo.h1_size); root.classList.add('typo-h1-custom'); } else { root.classList.remove('typo-h1-custom'); }
  if (typo.h2_size) { root.style.setProperty('--size-h2', typo.h2_size); root.classList.add('typo-h2-custom'); } else { root.classList.remove('typo-h2-custom'); }
  if (typo.h3_size) { root.style.setProperty('--size-h3', typo.h3_size); root.classList.add('typo-h3-custom'); } else { root.classList.remove('typo-h3-custom'); }
  if (typo.body_size) root.style.setProperty('--size-body', typo.body_size);
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  // Phase 133.79 Perf — Cold start: localStorage-Cache für /api/settings.
  // Settings ändern sich extrem selten (Branding, Nav, SEO, Pricing). Damit
  // Rückkehrer die App ohne „Lädt..."-Splash sehen, hydrieren wir aus
  // localStorage und revalidieren im Hintergrund. Erst-Besucher sehen weiter
  // den Splash (bis die erste echte Antwort da ist).
  const SETTINGS_LS_KEY = 'kev_settings_cache_v1';
  const SETTINGS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — Branding/Nav ändern sich extrem selten
  const _readCache = () => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SETTINGS_LS_KEY) : null;
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== 'object' || !obj.data || !obj.ts) return null;
      if (Date.now() - obj.ts > SETTINGS_TTL_MS) return null;
      return obj.data;
    } catch { return null; }
  };
  const initialCached = typeof window !== 'undefined' ? _readCache() : null;
  // Prefer the inline <head> prefetch (window.__PRELOADED_SETTINGS__) over
  // localStorage. The head script fires before React even mounts, so when
  // we arrive here the response is usually already in. First paint uses the
  // freshest server data without waiting for axios to spin up.
  const preloaded = typeof window !== 'undefined' ? window.__PRELOADED_SETTINGS__ : null;
  const [settings, setSettings] = useState(preloaded || initialCached);
  const [loading, setLoading] = useState(!(preloaded || initialCached));

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Prefer the inline-prefetched response if it finished before React
      // mounts. Otherwise axios makes a fresh request (will hit HTTP cache).
      let data = null;
      if (typeof window !== 'undefined' && window.__PRELOADED_SETTINGS_PROMISE__) {
        try { data = await window.__PRELOADED_SETTINGS_PROMISE__; } catch { data = null; }
        window.__PRELOADED_SETTINGS_PROMISE__ = null; // nur einmal nutzen – reload() nach Speichern muss frische Daten holen
      }
      if (!data) {
        const res = await axios.get(`${API}/settings`);
        data = res.data;
      }
      // Publish settings immediately so the first paint is the real branded
      // site (logo, hero, colors). Google-Reviews are non-critical and merged
      // in the background below — they must never delay the initial render.
      setSettings(data);
      // Persist für sofortigen Cold-Start beim nächsten Besuch
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(SETTINGS_LS_KEY, JSON.stringify({ data: data, ts: Date.now() }));
        }
      } catch { /* quota — nicht kritisch */ }

      // Google-Reviews summary — best-effort, merged when ready (non-blocking).
      axios
        .get(`${API}/reviews`, { timeout: 5000 })
        .then((reviewsRes) => {
          if (reviewsRes.data && (reviewsRes.data.rating || reviewsRes.data.user_ratings_total)) {
            setSettings((prev) =>
              prev
                ? {
                    ...prev,
                    reviews_summary: {
                      rating: reviewsRes.data.rating,
                      review_count: reviewsRes.data.user_ratings_total,
                    },
                  }
                : prev
            );
          }
        })
        .catch(() => {
          // ignore – reviews are non-critical for settings load
        });

      // Apply colors as CSS variables
      _applyBrandToRoot(data);
    } catch (error) {
      console.error('Failed to load settings', error);
    } finally {
      setLoading(false);
      // Splash entfernen, sobald wir Settings haben (oder sicher wissen, dass
      // keine kommen). Nutzt die window-Funktion aus index.html.
      try { if (typeof window !== 'undefined' && window.__kevHideSplash) window.__kevHideSplash(); } catch { /* noop */ }
    }
  };

  useEffect(() => {
    // Cold-Start Fast-Path: wenn wir aus localStorage/Preload hydriert haben, direkt
    // Branding anwenden (CSS-Variablen für Logo-Farben, Fonts etc.), damit die
    // sichtbare Seite nicht erst nach dem Settings-Fetch "umspringt".
    if (preloaded) _applyBrandToRoot(preloaded);
    else if (initialCached) _applyBrandToRoot(initialCached);
    // Splash sofort weg, sobald wir Settings parat haben (localStorage/Preload) —
    // der Hintergrund-Fetch läuft trotzdem noch, aber die App ist bereits sichtbar.
    if ((preloaded || initialCached) && typeof window !== 'undefined' && window.__kevHideSplash) {
      try { window.__kevHideSplash(); } catch { /* noop */ }
    }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avoid the "flash of empty content": hold the very first paint until the
  // settings (logo, hero, branding, colors) have arrived. Only gates the
  // initial load (settings === null); reload() never blanks the screen.
  if (loading && settings === null) {
    return (
      <div
        data-testid="settings-splash"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#005898',
          zIndex: 9999,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(255,255,255,0.25)',
            borderTopColor: '#fdc20f',
            borderRadius: '50%',
            animation: 'settings-splash-spin 0.8s linear infinite',
          }}
        />
        <style>{'@keyframes settings-splash-spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={{ settings, loading, reload: loadSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Resolves an image URL to a fully-qualified, optionally width-resized URL.
 * The backend's `/api/files/{path}` endpoint supports `?w=` for on-the-fly
 * resizing to {320,480,640,800,1024,1280,1600,1920} → returns optimised WebP.
 * Always pass `width` for cards / thumbnails so we don't transfer 1.5 MB
 * originals when 480 px is enough.
 */
export const resolveImageUrl = (url, width) => {
  if (!url) return '';
  const full = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : url.startsWith('/api/files/')
      ? `${BACKEND_URL}${url}`
      : url;
  if (!width) return full;
  // Only attach width to our own files endpoint
  if (!full.includes('/api/files/')) return full;
  const allowed = [320, 480, 640, 800, 1024, 1280, 1600, 1920];
  // Snap to the next-larger allowed bucket so the backend cache deduplicates.
  const w = allowed.find((x) => x >= width) || 1920;
  const sep = full.includes('?') ? '&' : '?';
  return `${full}${sep}w=${w}`;
};

/**
 * Build a responsive `srcset` string for sharp display on Retina/4K screens.
 * Example: `srcSet={resolveSrcSet(p.image_url, [320, 640, 960])}`
 */
export const resolveSrcSet = (url, widths = [320, 480, 640, 800, 1024]) => {
  if (!url) return undefined;
  if (!String(url).includes('/api/files/') && !url.startsWith('/api/files/')) return undefined;
  return widths.map((w) => `${resolveImageUrl(url, w)} ${w}w`).join(', ');
};
